// 📦 PrintStoreQR.js – Printable QR codes for store items
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { getDocs, collection } from "firebase/firestore";
import { QRCodeSVG } from "qrcode.react";
import { useSchool } from "../hooks/useSchool";

const PrintStoreQR = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const schoolId = useSchool();

  useEffect(() => {
    const fetchStoreItems = async () => {
      if (!schoolId) return;
      setLoading(true);
      setError("");
      try {
        const querySnapshot = await getDocs(
          collection(db, "schools", schoolId, "store")
        );
        const storeItems = querySnapshot.docs
          .map((itemDoc) => ({id: itemDoc.id, ...itemDoc.data()}))
          .filter((item) => item.available !== false)
          .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
        setItems(storeItems);
      } catch (err) {
        console.error("Store QR load failed:", err);
        setError("Store labels could not be loaded.");
      } finally {
        setLoading(false);
      }
    };

    fetchStoreItems();
  }, [schoolId]);

  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 300);
  };

  return (
    <>
      {/* 🖨️ Inline print styles */}
      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact;
            margin: 0;
            padding: 0;
          }

          .qr-card {
            page-break-inside: avoid;
            break-inside: avoid;
            margin-bottom: 1rem;
            border: 1px solid #000 !important;
            background: white;
          }

          button,
          nav,
          .navbar,
          .dashboard-container {
            display: none !important;
          }
        }
      `}</style>

      <div style={{ padding: "2rem" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h1>🖨️ Printable Store QR Codes</h1>
          <button
            onClick={handlePrint}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
            }}
          >
            Print
          </button>
        </div>

        {loading && <p>Loading store items…</p>}
        {error && <p role="alert">{error}</p>}
        {!loading && !error && items.length === 0 && <p>No available store items.</p>}

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            columnGap: "1.5rem",
            rowGap: "2rem",
            marginTop: "2rem",
            justifyContent: "center",
          }}
        >
          {items.map((item) => (
            <div
              key={item.id}
              className="qr-card"
              style={{
                width: "calc(33.333% - 1rem)",
                minWidth: "200px",
                border: "1px solid #ccc",
                padding: "1rem",
                borderRadius: "8px",
                textAlign: "center",
                backgroundColor: "#fff",
                pageBreakInside: "avoid",
              }}
            >
              {item.imageUrl && (
                <img
                  src={item.imageUrl}
                  alt={item.name}
                  style={{
                    width: "100%",
                    height: "100px",
                    objectFit: "contain",
                    marginBottom: "0.5rem",
                  }}
                />
              )}
              <h3 style={{ marginBottom: "0.25rem" }}>{item.name}</h3>
              <p
                style={{
                  fontSize: "1rem",
                  fontWeight: "bold",
                  marginBottom: "0.5rem",
                }}
              >
                🎟 {item.cost ?? item.ticketCost ?? 0} Tickets
              </p>
              <QRCodeSVG
                value={`${window.location.origin}/${schoolId}/store-mode?item=${item.id}`}
                size={128}
                includeMargin={true}
              />
              <p style={{ fontSize: "0.75rem", marginTop: "0.5rem" }}>
                Scan to Add to Cart
              </p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default PrintStoreQR;
